/**
 * TodayPage Component
 *
 * Single-Screen Intelligence Briefing - the post-login landing page
 * providing a consolidated view of conduct, complaint, and Consumer Duty
 * indicators from a rolling 24-hour window.
 *
 * Atomic level: Page
 * Composes: WelcomeMessage, MetricStrip, ActionCardsSection, ConsumerDutySection
 *
 * Design Constraints:
 * - All content must be visible without scrolling on desktop (single-screen)
 * - Responsive stacking on smaller screens with priority order
 * - Real-time updates via WebSocket
 */

import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTodayMetrics } from './hooks/useTodayMetrics';
import { useTodayWebSocket } from './hooks/useTodayWebSocket';
import {
  WelcomeMessage,
  MetricStrip,
  ActionCardsSection,
  // ConsumerDutySection, // Kept for potential revert
  RecentCollectionsSection,
} from './components';
import { TodayPageSkeleton } from './TodayPageSkeleton';
import { TodayPageError } from './TodayPageError';

type TodaySectionId = 'key-metrics' | 'priority-records' | 'recent-collections';

/**
 * Today Page - Single-Screen Intelligence Briefing
 *
 * Post-login landing page providing a consolidated view of conduct,
 * complaint, and Consumer Duty indicators from a rolling 24-hour window.
 *
 * Features:
 * - Real-time WebSocket updates
 * - Responsive layout with priority stacking
 * - Error handling with retry capability
 */
export function TodayPage(): React.ReactElement {
  // Subscribe to real-time updates
  useTodayWebSocket();

  const { data, isLoading, isError, error, refetch } = useTodayMetrics();
  const [activeSection, setActiveSection] = useState<TodaySectionId>('key-metrics');
  const keyMetricsRef = useRef<HTMLElement | null>(null);
  const priorityRecordsRef = useRef<HTMLElement | null>(null);
  const recentCollectionsRef = useRef<HTMLElement | null>(null);

  // Loading state
  if (isLoading) {
    return <TodayPageSkeleton />;
  }

  // Error state
  if (isError || !data) {
    return <TodayPageError error={error as Error | null} onRetry={refetch} />;
  }

  // Uncomment when re-enabling ConsumerDutySection below
  // const noData = data.metrics.volumeOfFeedback.current === 0;

  const handleSectionSelect = (section: TodaySectionId) => {
    setActiveSection(section);

    const target =
      section === 'key-metrics'
        ? keyMetricsRef.current
        : section === 'priority-records'
          ? priorityRecordsRef.current
          : recentCollectionsRef.current;

    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target?.focus({ preventScroll: true });
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[var(--go-spring)] text-foreground">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-4 py-4 md:gap-5 md:px-6 md:py-6 xl:px-8 xl:py-6">
        <WelcomeMessage greeting={data.greeting} />

        <nav className="flex flex-wrap gap-2" aria-label="Today sections">
          <button
            type="button"
            onClick={() => handleSectionSelect('key-metrics')}
            aria-pressed={activeSection === 'key-metrics'}
            className={`flex items-center gap-2 rounded-full border px-3 py-2 text-[13px] font-semibold transition-[background-color,color,border-color] ${
              activeSection === 'key-metrics'
                ? 'border-primary/15 bg-[var(--go-good-vibes-tint)] text-primary'
                : 'border-border bg-background text-muted-foreground hover:border-primary/15 hover:text-foreground'
            }`}
          >
            Key Metrics
          </button>
          <button
            type="button"
            onClick={() => handleSectionSelect('priority-records')}
            aria-pressed={activeSection === 'priority-records'}
            className={`flex items-center gap-2 rounded-full border px-3 py-2 text-[13px] font-semibold transition-[background-color,color,border-color] ${
              activeSection === 'priority-records'
                ? 'border-primary/15 bg-[var(--go-good-vibes-tint)] text-primary'
                : 'border-border bg-background text-muted-foreground hover:border-primary/15 hover:text-foreground'
            }`}
          >
            Priority Records
          </button>
          <button
            type="button"
            onClick={() => handleSectionSelect('recent-collections')}
            aria-pressed={activeSection === 'recent-collections'}
            className={`flex items-center gap-2 rounded-full border px-3 py-2 text-[13px] font-semibold transition-[background-color,color,border-color] ${
              activeSection === 'recent-collections'
                ? 'border-primary/15 bg-[var(--go-good-vibes-tint)] text-primary'
                : 'border-border bg-background text-muted-foreground hover:border-primary/15 hover:text-foreground'
            }`}
          >
            Recent Collections
          </button>
        </nav>

        <section
          id="key-metrics"
          ref={keyMetricsRef}
          tabIndex={-1}
          className="space-y-3 scroll-mt-6 rounded-2xl outline-none"
        >
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Key Metrics
            </h2>
            <span className="text-[11px] text-muted-foreground">Rolling 24 hours</span>
          </div>
          <MetricStrip metrics={data.metrics} />
        </section>

        <section
          id="priority-records"
          ref={priorityRecordsRef}
          tabIndex={-1}
          className="space-y-3 scroll-mt-6 rounded-2xl outline-none"
        >
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Priority Records
            </h2>
            <span className="text-[11px] text-muted-foreground">Needs attention now</span>
          </div>
          <ActionCardsSection
            eligibleComplaints={data.eligibleComplaints}
            vulnerabilityDetector={data.vulnerabilityDetector}
          />
        </section>

        <section
          id="recent-collections"
          ref={recentCollectionsRef}
          tabIndex={-1}
          className="space-y-3 pb-2 scroll-mt-6 rounded-2xl outline-none"
        >
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Recent Collections
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-muted-foreground">Newest first</span>
              <Link
                to="/v4/records"
                className="text-[12px] font-semibold text-primary transition-colors hover:text-primary/80"
              >
                View all
              </Link>
            </div>
          </div>
          <RecentCollectionsSection />
        </section>

        {/*
          Outcomes Overview Section - Currently disabled

          To re-enable:
          1. Uncomment the noData variable above
          2. Uncomment this section
          3. Enable the backend query in today_service.py (_get_consumer_duty_outcomes)
          4. Update today.py model to make consumer_duty_outcomes required again

        <section>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Outcomes Overview
          </h2>
          <ConsumerDutySection outcomes={data.consumerDutyOutcomes} noData={noData} />
          </section>
        */}
      </div>
    </div>
  );
}

export default TodayPage;
